<template lang="pug">
    div.live-calls-agent-index
        TableFixedHeader(
            v-if="validAgents"
            dark
            :items="agents"
            :num-num-of-sticky-columns="2"
            :row-class="getRowClassName"
            row-key="call_id"
        )

            template(v-slot:th-default)
                th.text-left.no-wrap.white--text Direction
                th.text-left.no-wrap.white--text Time
                th.text-left.no-wrap.white--text Actions
                th.text-left.no-wrap.white--text Agent
                th.text-left.no-wrap.white--text Organization
                th.text-left.no-wrap.white--text Campaign
            template(v-slot:tr-default="slotProps")
                td.no-wrap.sm-width
                    template(v-if="slotProps.item.agent_status_name === 'Incoming Call'") Inbound
                    template(v-else) Outbound
                td.no-wrap.sm-width
                    template(v-if="(slotProps.item.time_in_seconds || 0) < 600")
                        strong {{ (slotProps.item.time_in_seconds || 0) | secondsToFullWords }}

                    v-tooltip(top v-else)
                        template(v-slot:activator='{ on, attrs }')
                            span(v-bind="attrs" v-on="on" role="text")
                                Timeago(
                                    :datetime="slotProps.item.agent_created_at"
                                    :auto-update="60"
                                )
                        span {{ (slotProps.item.time_in_seconds || 0) | secondsToFullWords }}
                td.no-wrap.sm-md-width
                    v-btn.mr-2(small color="info" dark @click="superviseCall('listen', slotProps.item)" :loading="isLoadingForAgent(slotProps.item.call_id, 'listen')")
                        v-icon mdi-ear-hearing
                        | Listen
                    v-btn.ml-2(small color="warning" dark @click="superviseCall('barge', slotProps.item)" :loading="isLoadingForAgent(slotProps.item.call_id, 'barge')")
                        v-icon mdi-microphone
                        | Barge
                    v-btn.ml-4.mr-2(v-if="slotProps.item.communication_provider_id == communicationProviderTelnyxId" small color="success" dark @click="superviseCall('coaching', slotProps.item)")
                        v-icon mdi-account-supervisor
                        | Coach
                td.no-wrap.sm-width {{ slotProps.item.user_full_name }}
                td.no-wrap.sm-width
                    v-tooltip(top v-if="slotProps.item.organization_name && slotProps.item.organization_name.length > 0")
                        template(v-slot:activator='{ on, attrs }')
                            span(v-bind="attrs" v-on="on" role="text")
                                | {{ slotProps.item.organization_name | str_limit(35) }}
                        span 
                            | {{ slotProps.item.organization_name }}
                    template(v-else) N/a
                td.no-wrap.sm-width
                    template(v-if="slotProps.item.campaign_id > 0")
                        v-tooltip(top)
                            template(v-slot:activator='{ on, attrs }')
                                span(v-bind="attrs" v-on="on" role="text")
                                    | {{ slotProps.item.campaign_name | str_limit(35) }}
                            span
                                | {{ slotProps.item.campaign_name }}
                    template(v-else) N/a

        v-alert(type="error" v-else dense)
            | Zero agents on active calls

</template>

<script lang="ts">
    import Vue from 'vue';

    // Data 
    import { SuperviseCall } from "@/data";

    // Components
    import TableFixedHeader from '@/components/shared/table_fixed_header.vue';

    export default Vue.extend({
        data() {
            return {
                communicationProviderTelnyxId: null,
                firstColumnWidth: 175,
                loadingStates: {}, // Map to track loading state per agent and action
            }
        },
        methods: {
            getClassName(seconds) {
                // alert error
                if (seconds >= 300) {
                    return "alert-error-background-color";
                }

                // alert warning
                if (seconds >= 180) {
                    return "alert-warning-background-color";
                }

                // alert success
                return "alert-success-background-color"
            },
            getRowClassName(item) {
                return this.getClassName(item?.time_in_seconds);
            },
            loadProviderIds() {
                // Load provider ids
                if (import.meta.env.VITE_APP_COMMUNICATION_PROVIDER_TELNYX_ID?.length > 0) {
                    this.communicationProviderTelnyxId = parseInt(import.meta.env.VITE_APP_COMMUNICATION_PROVIDER_TELNYX_ID);
                }
            },
            isLoadingForAgent(callId: number, action: string): boolean {
                return this.loadingStates[`${callId}-${action}`] === true;
            },
            setLoadingForAgent(callId: number, action: string, loading: boolean) {
                this.$set(this.loadingStates, `${callId}-${action}`, loading);
            },
            superviseCall(role, agentRecord) {

                const options = {
                    role: role,
                }
                
                // Set loading state for this specific call and action
                this.setLoadingForAgent(agentRecord.call_id, role, true);

                const call_id = agentRecord.call_id;
                SuperviseCall(call_id, options).then((response) => {
                    if (response?.Data?.ok === true) {
                        if (response?.Data?.call?.assigned_campaign_customer?.campaign_customer_active_id > 0 || response?.Data?.call?.assigned_campaign_customer?.campaign_customer_id > 0) {
                            this.setCurrentCampaignCustomer(response.Data.call.assigned_campaign_customer);
                        } else {
                            this.setAsCallWithoutCustomer()
                        }
                    
                        // Bubble up role
                        this.setSupervisorRole(role)
                        if (response.Data.call?.call?.id > 0) {
                            this.setCurrentCall(response.Data.call)
                        }

                        if (response.Data.web_rtc_participant?.id > 0 && response.Data.web_rtc_session?.id > 0) {
                            this.setCurrentWebRtcParticipant(response.Data.web_rtc_participant)
                            this.setCurrentWebRtcSession(response.Data.web_rtc_session)
                        }
                    }
                    // Clear loading state for this specific call and action
                    this.setLoadingForAgent(agentRecord.call_id, role, false);
                }).catch((error) => {
                    // Also clear loading state on error
                    this.setLoadingForAgent(agentRecord.call_id, role, false);
                });
            },
        },
        computed: {
            validAgents() {
                return this.agents?.length > 0;
            },
        },
        components: {
            TableFixedHeader
        },
        props: {
            agents: Array,
            setSupervisorRole: Function,
            setAsCallWithoutCustomer: Function,
            setCurrentCall: Function,
            setCurrentCampaignCustomer: Function,
            setCurrentWebRtcParticipant: Function,
            setCurrentWebRtcSession: Function,
        },
        watch: {
        },
        created() {
            this.loadProviderIds();
        },
    });
</script>

<style lang="scss" scoped>
    :deep(tr.alert-error-background-color),
    :deep(tr.alert-warning-background-color),
    :deep(tr.alert-success-background-color) {
        color: white !important;
    }
    :deep(tr.alert-error-background-color) {
        background-color: var(--error) !important;
    }
    :deep(tr.alert-warning-background-color) {
        background-color: var(--orange) !important;
    }
    :deep(tr.alert-success-background-color) {
        background-color: var(--success) !important;
    }
</style>

<style lang="scss">
    // Must include #app to beat styles.scss (global rules are nested under #app { ... }).
    #app .live-calls-agent-index .theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr.alert-error-background-color:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
        background: linear-gradient(rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.15)), var(--error) !important;

        td {
            color: white !important;
            background: transparent !important;
        }
    }
    #app .live-calls-agent-index .theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr.alert-warning-background-color:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
        background: linear-gradient(rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.15)), var(--orange) !important;

        td {
            color: white !important;
            background: transparent !important;
        }
    }
    #app .live-calls-agent-index .theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr.alert-success-background-color:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
        background: linear-gradient(rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.15)), var(--success) !important;

        td {
            color: white !important;
            background: transparent !important;
        }
    }
</style>